import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, Loader2Icon } from "lucide-react";
import { btnPrimary, ease } from "../utils/styles";
type Status = "idle" | "loading" | "success";
const materials = ["Hout", "Acryl", "3D print", "Weet ik niet"];
interface FormState {
  name: string;
  email: string;
  message: string;
}
export function RequestForm({
  onDone
}: {
  onDone?: () => void;
}) {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    message: ""
  });
  const [material, setMaterial] = useState("Weet ik niet");
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<Status>("idle");
  const update = (key: keyof FormState, value: string) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    if (errors[key]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = "Vul je naam in";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Vul een geldig e-mailadres in";
    if (form.message.trim().length < 10) next.message = "Vertel iets meer over je idee";
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus("loading");
    window.setTimeout(() => setStatus("success"), 900);
  };
  const field = "w-full rounded-2xl bg-sand px-4 text-[15px] text-ink placeholder:text-muted/80 focus:bg-paper focus:outline-none focus:ring-2 focus:ring-ink/80 transition-colors duration-150";
  return <AnimatePresence mode="wait" initial={false} data-id="element-358" data-mp="3ipn95c0ztk88">
      {status === "success" ? <motion.div key="success" initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease
    }} className="flex flex-col items-center py-10 text-center" role="status" data-id="element-359" data-mp="3eqeem4y4pibm">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ink text-paper" data-id="element-360" data-mp="2le6m00s1d4x6">
            <CheckIcon className="h-6 w-6" data-id="element-361" data-mp="2peqjj5giuaju" />
          </span>
          <h3 className="mt-5 text-2xl font-medium tracking-[-0.02em] text-ink" data-id="element-362" data-mp="2ud1q97bis4xu">Bedankt, {form.name.split(" ")[0]}!</h3>
          <p className="mt-2 text-[15px] text-muted" data-id="element-363" data-mp="2z4s6ftx51mx6">We nemen contact op via {form.email}.</p>
          {onDone && <button type="button" onClick={onDone} className={`${btnPrimary} mt-6`} data-id="element-364" data-mp="342toqcmqpi7l">
              Sluiten
            </button>}
        </motion.div> : <motion.form key="form" onSubmit={submit} noValidate initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.18
    }} className="grid gap-3 sm:grid-cols-2" data-id="element-365" data-mp="3d4ull8e2a28q">
          <div data-id="element-366" data-mp="1ir200gijqmkd">
            <input aria-label="Naam" value={form.name} onChange={e => update("name", e.target.value)} placeholder="Naam" className={`${field} h-12 ${errors.name ? "ring-2 ring-red-600" : ""}`} data-id="element-367" data-mp="1irbi7hxcnpfi" />
            {errors.name && <p className="mt-1 px-2 text-xs text-red-700" data-id="element-368" data-mp="1g7f2ote4ty2j">{errors.name}</p>}
          </div>
          <div data-id="element-369" data-mp="1bdawwdglrw31">
            <input aria-label="E-mail" type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="E-mail" className={`${field} h-12 ${errors.email ? "ring-2 ring-red-600" : ""}`} data-id="element-370" data-mp="1bcsqwp4523hx" />
            {errors.email && <p className="mt-1 px-2 text-xs text-red-700" data-id="element-371" data-mp="1z4q0sao12lge">{errors.email}</p>}
          </div>
          <fieldset className="sm:col-span-2" data-id="element-372" data-mp="1u4ljiifapmp4">
            <legend className="mb-2 px-1 text-sm text-muted" data-id="element-373" data-mp="1u5myuz3cgvcn">Materiaal</legend>
            <div className="flex flex-wrap gap-2" data-id="element-374" data-mp="1p4z2rz09dobk">
              {materials.map(m => <button key={m} type="button" aria-pressed={material === m} onClick={() => setMaterial(m)} className={`h-10 rounded-full px-4 text-sm transition-colors duration-150 ${material === m ? "bg-ink text-paper" : "bg-sand text-ink hover:bg-stone"}`} data-id="element-375" data-mp="1l3dme3oimrwm">
                  {m}
                </button>)}
            </div>
          </fieldset>
          <div className="sm:col-span-2" data-id="element-376" data-mp="1no1wsswskwsh">
            <textarea aria-label="Jouw wens" rows={4} value={form.message} onChange={e => update("message", e.target.value)} placeholder="Vertel ons je idee: tekst of naam, formaat, kleur…" className={`${field} resize-none py-3 ${errors.message ? "ring-2 ring-red-600" : ""}`} data-id="element-377" data-mp="1np372v5zhf95" />
            {errors.message && <p className="mt-1 px-2 text-xs text-red-700" data-id="element-378" data-mp="27azk0zt5loza">{errors.message}</p>}
          </div>
          <button type="submit" disabled={status === "loading"} className={`${btnPrimary} sm:col-span-2`} data-id="element-379" data-mp="2bcbfvn2mf6fx">
            {status === "loading" && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-380" data-mp="vyp0lk0av57i" />}
            {status === "loading" ? "Versturen…" : "Verstuur je wens"}
          </button>
        </motion.form>}
    </AnimatePresence>;
}